import type { CSSProperties, ElementType, ReactNode } from 'react'

import { createElement, useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, isCompactViewport, prefersReducedMotion, registerGsap } from '@/lib/gsap'

type RevealVariant = 'move' | 'fade' | 'scale' | 'blur'

interface RevealProps {
    as?: ElementType
    variant?: RevealVariant
    /** Reveal delay in seconds (Webflow default is 0.1s). */
    delay?: number
    /** Reveal duration in seconds (Webflow default is 1s). */
    duration?: number
    /** Travel distance in px for the `move` variant. */
    distance?: number
    /** Start scale for the `scale` variant. */
    fromScale?: number
    /** ScrollTrigger start position. */
    start?: string
    className?: string
    style?: CSSProperties
    children?: ReactNode
    [key: string]: unknown
}

/**
 * Recreates Webflow's "Fade In & Move/Scale/Blur On Scroll" interactions
 * (action lists a-11 / a-12 / a-13 / a-14) as one polymorphic component.
 *
 * The hidden state lives in CSS (`.js-reveal`) so server and client render
 * identically and GSAP solely owns the inline styles once it animates — a
 * React re-render can never re-hide already-revealed content.
 */
export const Reveal = ({
    as = 'div',
    variant = 'move',
    delay = 0.1,
    duration = 1,
    distance = 25,
    fromScale = 0.5,
    start = 'top 88%',
    className,
    style,
    children,
    ...rest
}: RevealProps) => {
    const ref = useRef<HTMLElement | null>(null)

    useEffect(() => {
        registerGsap()
        const el = ref.current
        if (!el) return
        if (prefersReducedMotion() || isCompactViewport()) return

        const fromVars: gsap.TweenVars = { opacity: 0 }
        const toVars: gsap.TweenVars = {
            opacity: 1,
            duration,
            delay,
            ease: 'wfEase',
            scrollTrigger: { trigger: el, start, once: true }
        }

        if (variant === 'move') {
            fromVars.y = distance
            toVars.y = 0
        }
        if (variant === 'scale') {
            fromVars.scale = fromScale
            toVars.scale = 1
        }
        if (variant === 'blur') {
            fromVars.filter = 'blur(6px)'
            toVars.filter = 'blur(0px)'
        }

        const ctx = gsap.context(() => {
            gsap.fromTo(el, fromVars, toVars)
        }, el)

        return () => ctx.revert()
    }, [variant, delay, duration, distance, fromScale, start])

    const initStyle: CSSProperties = {}
    if (variant === 'scale') (initStyle as Record<string, string | number>)['--reveal-scale'] = fromScale
    if (variant === 'move') (initStyle as Record<string, string | number>)['--reveal-distance'] = `${distance}px`

    return createElement(
        as,
        {
            ref,
            className: cn('js-reveal', className),
            'data-variant': variant,
            style: { ...initStyle, ...style },
            ...rest
        },
        children
    )
}
